/**
 * @file Health component.
 *
 * Holds numbers only. Damage is never applied by whoever caused it: systems
 * emit a `damage` event and {@link ../systems/HealthSystem.js} is the single
 * place that mutates these fields.
 *
 * Two flavours share the component, so every system that already knows how to
 * hurt or kill something keeps working unchanged:
 *  - hit points (`max`/`current`) — enemies and anything else destructible;
 *  - a kill countdown (`killTimer`) — the player, who has no hit points at all
 *    and instead lives on seconds that only a kill refills.
 */

import { Component } from '../ecs/Component.js';

export class Health extends Component {
  /**
   * @param {number} [max] Maximum hit points.
   * @param {number} [invulnerabilityTime] Seconds of immunity after a hit.
   */
  constructor(max = 1, invulnerabilityTime = 0) {
    super();
    /** @type {number} */
    this.max = max;
    /** @type {number} */
    this.current = max;
    /** @type {number} Seconds of immunity granted per hit. */
    this.invulnerabilityTime = invulnerabilityTime;
    /** @type {number} Remaining immunity, counted down by the health system. */
    this.invulnerableFor = 0;
    /** @type {boolean} Set once current health reaches zero. */
    this.dead = false;
    /** @type {number} Seconds since the last hit, drives the hit flash. */
    this.timeSinceHit = Infinity;
    /** @type {number} Countdown ceiling in seconds; 0 on hit-point entities. */
    this.killTimerMax = 0;
    /** @type {number} Seconds left before the countdown kills its owner. */
    this.killTimer = 0;
    /** @type {number} Seconds drained from the countdown per point of damage. */
    this.killTimerDamagePenalty = 0;
  }

  /**
   * Builds a countdown-driven pool: no hit points, just seconds that drain in
   * real time and are refilled by every kill.
   *
   * @param {number} killTimerMax Countdown ceiling, in seconds.
   * @param {number} damagePenalty Seconds lost per point of damage taken.
   * @param {number} [invulnerabilityTime] Seconds of immunity after a hit.
   * @returns {Health}
   */
  static countdown(killTimerMax, damagePenalty, invulnerabilityTime = 0) {
    const health = new Health(0, invulnerabilityTime);
    health.killTimerMax = killTimerMax;
    health.killTimer = killTimerMax;
    health.killTimerDamagePenalty = damagePenalty;
    return health;
  }

  /** @returns {boolean} Whether seconds, not hit points, keep this entity alive. */
  get usesKillTimer() {
    return this.killTimerMax > 0;
  }

  /** @returns {number} Remaining life as a 0..1 ratio. */
  get ratio() {
    if (this.usesKillTimer) return Math.max(0, this.killTimer / this.killTimerMax);
    return this.max > 0 ? this.current / this.max : 0;
  }

  /** @returns {void} */
  reset() {
    this.current = this.max;
    this.killTimer = this.killTimerMax;
    this.invulnerableFor = 0;
    this.dead = false;
    this.timeSinceHit = Infinity;
  }
}
